import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';

interface Match {
  id: string;
  matchDate: string;
  homeTeam: {
    name: string;
    shortName: string;
    logoUrl: string;
  };
  awayTeam: {
    name: string;
    shortName: string;
    logoUrl: string;
  };
  season: {
    name: string;
    gameType: string;
  };
  matchGroup: {
    name: string;
    matchWeek: number;
  };
}

interface NewsletterPreview {
  matchesBySeason: Record<string, Match[]>;
  totalSeasons: number;
  totalMatches: number;
}

const AdminNewsletter: React.FC = () => {
  const [formData, setFormData] = useState({
    subject: '',
    bodyText: '',
    headerImageUrl: '' as string | undefined
  });
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sendingProgress, setSendingProgress] = useState<{
    current: number;
    total: number;
    status: string;
  } | null>(null);

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await apiClient.get('/admin/newsletter/preview');
      setPreview(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview data');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUploadHeader = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('logo', selectedFile);

      const response = await apiClient.post('/admin/newsletter/upload-header', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        headerImageUrl: response.data.imageUrl
      }));
      setSelectedFile(null);
      setMessage('Header image uploaded successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload header image');
    } finally {
      setUploading(false);
    }
  };

  const handleSendTest = async () => {
    if (!formData.subject.trim() || !formData.bodyText.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      setError('');
      setMessage('');

      const response = await apiClient.post('/admin/newsletter/send', {
        ...formData,
        headerImageUrl: formData.headerImageUrl && formData.headerImageUrl.length > 0 ? formData.headerImageUrl : undefined,
        testMode: true
      });

      if (response.data.totalAdmins > 1) {
        setMessage(`Test newsletter sent to ${response.data.totalAdmins} admin users (${response.data.successCount} successful, ${response.data.failureCount} failed)`);
      } else {
        setMessage(`Test newsletter sent successfully to ${response.data.recipients[0].email}`);
      }
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send test newsletter');
    } finally {
      setSending(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!formData.subject.trim() || !formData.bodyText.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!window.confirm('Are you sure you want to send this newsletter to ALL users? This action cannot be undone.')) {
      return;
    }

    try {
      setSending(true);
      setError('');
      setMessage('');
      // Initialize progress
      setSendingProgress({
        current: 0,
        total: 100,
        status: 'Preparing to send newsletter...'
      });

      // Simulate progress updates since we can't get real-time updates
      const progressInterval = setInterval(() => {
        setSendingProgress(prev => {
          if (!prev) return null;
          // More realistic progress simulation
          const increment = Math.random() * 8 + 2; // 2-10% increments
          const newCurrent = Math.min(prev.current + increment, 85);
          const status = newCurrent < 30 ? 'Preparing email batches...' :
                        newCurrent < 60 ? 'Sending emails in batches...' :
                        newCurrent < 85 ? 'Processing final batches...' : 'Finalizing...';
          
          return {
            current: Math.floor(newCurrent),
            total: 100,
            status: `${status} (${Math.floor(newCurrent)}% complete)`
          };
        });
      }, 1500);

      const response = await apiClient.post('/admin/newsletter/send', {
        ...formData,
        headerImageUrl: formData.headerImageUrl && formData.headerImageUrl.length > 0 ? formData.headerImageUrl : undefined,
        testMode: false
      }, {
        timeout: 300000 // 5 minutes timeout for large email sends
      });

      // Clear progress interval
      clearInterval(progressInterval);

      // Show detailed results
      const { successCount, failureCount, totalUsers, validUsers, invalidEmails, failedEmails, successfulEmails, batchCount } = response.data;
      
      // Update progress to complete
      setSendingProgress({
        current: 100,
        total: 100,
        status: 'Newsletter sending completed!'
      });
      
      let messageText = '';
      if (invalidEmails > 0) {
        messageText += `📧 Skipped ${invalidEmails} invalid emails. `;
      }
      
      if (failureCount === 0) {
        messageText += `🎉 Newsletter sent successfully to all ${successCount} valid users!`;
        setMessage(messageText);
      } else if (successCount === 0) {
        messageText += `❌ Failed to send newsletter to any users. ${failureCount} failures.`;
        setError(messageText);
      } else {
        messageText += `⚠️ Newsletter sent to ${successCount} users with ${failureCount} failures.`;
        setMessage(messageText);
      }
      
      // Show detailed results in console for debugging
      console.log('Newsletter Results:', {
        totalUsers,
        successCount,
        failureCount,
        batchCount,
        failedEmails: failedEmails?.slice(0, 5), // Show first 5 failures
        successfulEmails: successfulEmails?.slice(0, 5) // Show first 5 successes
      });
      
      // Only clear form if completely successful
      if (failureCount === 0) {
        setFormData({
          subject: '',
          bodyText: '',
          headerImageUrl: ''
        });
      }
      
      // Clear message and progress after 10 seconds for better readability
      setTimeout(() => {
        setMessage('');
        setSendingProgress(null);
      }, 10000);
    } catch (err: any) {
      console.error('Newsletter send error:', err);
      console.error('Error response:', err.response?.data);
      
      let errorMessage = 'Failed to send newsletter';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = `Newsletter error: ${err.response.data.error}`;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
      setSendingProgress(null);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Loading newsletter page...</div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Admin Dashboard
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Weekly Newsletter
          </h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Send weekly newsletters to all users with upcoming matches
          </p>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {message}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Newsletter Form */}
          <div className="card rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
              Compose Newsletter
            </h2>

            <form onSubmit={(e) => e.preventDefault()}>
              {/* Subject */}
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Subject Line *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter newsletter subject..."
                  maxLength={200}
                  required
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {formData.subject.length}/200 characters
                </p>
              </div>

              {/* Header Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Header Image (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={handleUploadHeader}
                    disabled={!selectedFile || uploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
                {formData.headerImageUrl && formData.headerImageUrl.length > 0 && (
                  <div className="mt-2">
                    <img 
                      src={formData.headerImageUrl} 
                      alt="Header preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                )}
              </div>

              {/* Body Text */}
              <div className="mb-6">
                <label htmlFor="bodyText" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Newsletter Body *
                </label>
                <textarea
                  id="bodyText"
                  name="bodyText"
                  value={formData.bodyText}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Write your newsletter content here..."
                  maxLength={5000}
                  required
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {formData.bodyText.length}/5000 characters
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={sending || !formData.subject.trim() || !formData.bodyText.trim()}
                  className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send Test'}
                </button>
                <button
                  type="button"
                  onClick={handleSendNewsletter}
                  disabled={sending || !formData.subject.trim() || !formData.bodyText.trim()}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send to All Users'}
                </button>
              </div>

              {/* Progress Indicator */}
              {sendingProgress && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">
                      {sendingProgress.status}
                    </span>
                    <span className="text-sm text-blue-600">
                      {sendingProgress.current} / {sendingProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(sendingProgress.current / sendingProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Sending emails in batches. This may take a few minutes for large user lists.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Preview Section */}
          <div className="card rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
              Preview
            </h2>

            {previewLoading ? (
              <div className="text-center py-8">
                <div className="text-lg">Loading preview...</div>
              </div>
            ) : preview ? (
              <div>
                {/* Newsletter Preview */}
                <div className="mb-6 p-4 border rounded-lg">
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {formData.subject || 'Newsletter Subject'}
                  </h3>
                  {formData.headerImageUrl && formData.headerImageUrl.length > 0 && (
                    <img 
                      src={formData.headerImageUrl} 
                      alt="Header" 
                      className="w-full h-24 object-cover rounded mb-3"
                    />
                  )}
                  <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                    {formData.bodyText || 'Newsletter content will appear here...'}
                  </div>
                </div>

                {/* Upcoming Matches Preview */}
                <div className="mb-4">
                  <h3 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                    ⚽ This Week's Matches ({preview.totalMatches} matches across {preview.totalSeasons} seasons)
                  </h3>
                  
                  {Object.entries(preview.matchesBySeason).map(([seasonName, matches]) => (
                    <div key={seasonName} className="mb-4">
                      <h4 className="font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {seasonName}
                      </h4>
                      {matches.map((match) => (
                        <div key={match.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                          <div className="flex items-center gap-2">
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {match.homeTeam.name}
                            </span>
                            <span style={{ color: 'var(--text-secondary)' }}>vs</span>
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {match.awayTeam.name}
                            </span>
                          </div>
                          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {formatDate(match.matchDate)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <button
                  onClick={fetchPreview}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Refresh Preview
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-lg text-red-600">Failed to load preview</div>
                <button
                  onClick={fetchPreview}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNewsletter;
